<template>
  <CContainer fluid class="datatable-container">
    <Loader :is-processing="isLoading" />
    <!-- Start Modals -->
    <!-- User avatar modal -->
    <CModal
      backdrop="static"
      :visible="!!activeAvatar"
      :close-on-backdrop="false"
    >
      <div class="pt-4 px-4 create-popup-header">
        <div
          class="d-inline-block bg-gradient-blue text-white rounded-circle p-1 lh-normal cursor-pointer"
          @click="activeAvatar = null"
        >
          <span class="close-icon" />
        </div>
      </div>
      <div v-if="activeAvatar" class="pb-3 px-3">
        <img class="img-fluid" :src="activeAvatar" alt="" />
      </div>
      <template #footer-wrapper>
        <div />
      </template>
    </CModal>
    <!-- Archive message -->
    <CModal
      backdrop="static"
      :visible="!!showArchivedUsersModal"
      :close-on-backdrop="false"
    >
      <template #header-wrapper>
        <div
          class="pt-4 px-4 create-popup-header d-flex align-items-center justify-content-between"
        >
          <div
            class="d-inline-block bg-gradient-blue text-white rounded-circle p-1 lh-normal cursor-pointer"
            @click=";(showArchivedUsersModal = null), resetResponseMessages()"
          >
            <span class="close-icon" />
          </div>
        </div>
      </template>
      <div class="px-3 pt-3 pb-1">
        <div>
          {{ reqResponse.message }}
        </div>
        <div class="text-center mt-3">
          <button
            @click=";(showArchivedUsersModal = null), resetResponseMessages()"
            class="btn p-3 bg-gradient-blue text-white ms-2"
          >
            Okay
          </button>
        </div>
      </div>
      <template #footer-wrapper>
        <div />
      </template>
    </CModal>
    <!-- End Modals -->
    <CRow class="mt-5">
      <CCol>
        <div
          class="table-tabs-header mb-2 bg-white d-flex align-items-center justify-content-between"
        >
          <div class="d-flex align-items-center">
            <div class="me-4">
              <div
                v-for="(tab, index) in tableTabs"
                :key="index"
                class="table-tab"
                :class="{ active: tab.value === activeTableTab.value }"
              >
                {{ tab.label }}
              </div>
            </div>

            <div v-if="authTypes" class="d-flex justify-content-center">
              <div
                v-for="(authType, index) in authTypes"
                :key="index"
                class="auth-type-icon"
              >
                <span
                  v-if="authType.icon"
                  :class="'flaticon-' + authType.icon"
                  :title="authType.title"
                >
                  <span v-if="authType.html" v-html="authType.html" />
                </span>
              </div>
            </div>

            <div
              v-if="isActiveSyncVisible && isActiveSyncBtn"
              class="d-flex flex-column"
              @click="syncUsers(synchronization.id)"
            >
              <CLoadingButton
                :spinner="isLoadingBtn"
                :loading="isLoadingBtn"
                class="user-table-custom-btn d-flex m-auto px-4 py-2"
              >
                <span class="align-content-center">{{ syncButtonText }}</span>
              </CLoadingButton>
              <span class="fw-bold font-xs mt-1"
                >Last sync:
                {{ synchronization && synchronization.sync_finish_at }}</span
              >
            </div>
            <div
              v-if="!isActiveSyncBtn && isSuperAdmin"
              class="d-flex flex-column"
            >
              <CLoadingButton
                :spinner="true"
                :loading="true"
                :disabled="true"
                class="user-table-custom-btn disabled d-flex m-auto px-4 py-2"
              >
                <span class="align-content-center">{{ syncButtonText }}</span>
              </CLoadingButton>
            </div>
          </div>

          <div class="actions d-flex align-items-center">
            <div
              class="lh-normal cursor-pointer text-accent-blue-light ms-2 ms-sm-0"
              @click="handleUsers(pagination.activePage, false, false, true)"
            >
              <i class="fi flaticon-download me-1" />
              <span class="d-none d-md-inline-block">Download CSV</span>
            </div>

            <div
              class="d-flex align-items-center search-field mb-0 ms-2 ms-md-4"
            >
              <CInputGroup class="w-auto">
                <div class="input-group-prepend">
                  <span class="input-group-text lh-normal"
                    ><div><i class="flaticon-magnifying-glass"></i></div
                  ></span>
                </div>
                <CFormInput
                  placeholder="Search"
                  v-model="columnSearchKeys.globalQuery"
                  @input="searchByQuery($event.target.value)"
                >
                </CFormInput>
              </CInputGroup>
            </div>
          </div>
        </div>
        <div class="status-guides-container d-flex flex-wrap">
          <div
            class="d-flex align-items-center actions ps-0 ps-sm-3 mb-3 mb-sm-0"
          >
            <input
              v-if="!provideUsersByApiOnly"
              type="checkbox"
              class="me-2"
              @change="selectUsers($event)"
            />

            <CButton
              v-if="!provideUsersByApiOnly"
              class="me-2 p-small bg-gradient-blue gradient-btn"
              @click="inviteBulk()"
            >
              <span class="btn bg-white px-3 py-1 text-uppercase"
                ><b class="text-graident-blue">Reset Password</b></span
              >
            </CButton>

            <CButton
              v-if="!provideUsersByApiOnly"
              class="me-2 p-small bg-gradient-blue gradient-btn"
              @click="archive()"
            >
              <span class="btn bg-white px-3 py-1"
                ><b class="text-graident-blue text-uppercase">Archive</b></span
              >
            </CButton>

            <HelpCenterButton
              v-if="!provideUsersByApiOnly"
              width="18"
              height="18"
              classes="me-2 mt-2"
              content-key="login_type_info"
            />
            <CCheckBox
              label="Student Number"
              v-model="filterParams.student_sis_id"
              name="Student number"
              class="custom-checkbox"
              :inline="true"
              :custom="true"
            />
          </div>

          <per-page-selector :action="setPerPage" :pagination="pagination" />
        </div>

        <CAlert
          v-if="reqResponse.message"
          class="mt-1 w-100 mx-3"
          :color="reqResponse.type"
          :show="!!reqResponse.message"
        >
          {{ reqResponse.message }}
        </CAlert>

        <DataTable
          class="datatable remove-first-th-border bg-white"
          :items="users"
          :fields="activeDataFields"
          :column-filter="true"
          :sorter="{ external: true }"
          :no-items-view="{
            noResults: 'No items available',
            noItems: 'No items available'
          }"
          @update:sorter-value="sortByColumn"
        >
          <template #select="{ item }">
            <td class="mw-auto">
              <input
                v-if="!provideUsersByApiOnly"
                v-model="selectedUsers"
                type="checkbox"
                :value="item.id"
              />
            </td>
          </template>

          <template #new="{ item }">
            <td class="mw-auto">
              <button
                v-if="!item.is_synced"
                class="px-3 py-1 btn btn-unarchive d-flex align-items-center border-0"
                @click="recreateUser(item)"
              >
                <span class="flaticon-back-arrow" />
                <span class="ms-1 text-uppercase fw-bold">Sync</span>
              </button>

              <b
                v-if="getUserStatus(item)"
                class="px-3 py-2 bg-gradient-blue reverse text-white fw-bold text-uppercase"
                :style="`border-radius: 13.5px;font-size:11px;display: inline-block; width: ${
                  getUserStatus(item).length > 7 ? '100px' : 'auto'
                }`"
                >{{ getUserStatus(item) }}</b
              >
            </td>
          </template>

          <template #student_sis_id="{ item }">
            <td>
              <div>
                {{ item && item.student_sis_id ? item.student_sis_id : "" }}
              </div>
            </td>
          </template>

          <template #first_name="{ item }">
            <td
              :class="{
                'text-accent-blue-light fw-bold cursor-pointer': item.avatar
              }"
              @click="showAvatar(item.avatar)"
            >
              {{ item.first_name }}
            </td>
          </template>

          <template #last_name="{ item }">
            <td
              :class="{
                'text-accent-blue-light fw-bold cursor-pointer': item.avatar
              }"
              @click="showAvatar(item.avatar)"
            >
              {{ item.last_name }}
            </td>
          </template>

          <template #first_name-filter>
            <input
              v-model="columnSearchKeys.first_name.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #last_name-filter>
            <input
              v-model="columnSearchKeys.last_name.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #email="{ item }">
            <td>
              {{ item.email ? item.email : "" }}
            </td>
          </template>

          <template #email-filter>
            <input
              v-model="columnSearchKeys.email.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #action="{ item }">
            <td class="mw-auto">
              <button
                v-if="!provideUsersByApiOnly"
                class="btn btn-edit me-2 px-2 py-1"
                @click="editUser(item)"
              >
                <i class="flaticon-edit me-2" /> edit
              </button>
            </td>
          </template>

          <template #status="{ item }">
            <td>
              <b
                class="limit-status no-target d-block text-center w-full"
                :class="item.status ? 'active' : 'inactive-gray'"
                >{{ item.status ? "active" : "inactive" }}</b
              >
            </td>
          </template>

          <template #role_id="{ item }">
            <td>
              {{
                item.current_role_in_school
                  ? item.current_role_in_school.name
                  : ""
              }}
            </td>
          </template>

          <template #role_id-filter>
            <input
              v-model="columnSearchKeys.role_id.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #grade_year="{ item }">
            <td>
              {{ item.grade_year ? item.grade_year : "" }}
            </td>
          </template>

          <template #grade_year-filter>
            <input
              v-model="columnSearchKeys.grade_year.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #auth_type="{ item }">
            <td>
              {{ getAuthType(item.auth_type) }}
            </td>
          </template>

          <template #auth_type-filter>
            <input
              v-model="columnSearchKeys.auth_type.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #web_last_login_at="{ item }">
            <td class="text-center">
              {{ item.web_last_login_at ? item.web_last_login_at : "None" }}
            </td>
          </template>
          <template #web_last_login_at-filter>
            <input
              v-model="columnSearchKeys.web_last_login_at.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #mobile_last_login_at="{ item }">
            <td class="text-center">
              {{
                item.mobile_last_login_at ? item.mobile_last_login_at : "None"
              }}
            </td>
          </template>
          <template #mobile_last_login_at-filter>
            <input
              v-model="columnSearchKeys.mobile_last_login_at.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #mobile_last_used_at="{ item }">
            <td class="text-center">
              {{ item.mobile_last_used_at ? item.mobile_last_used_at : "None" }}
            </td>
          </template>
          <template #mobile_last_used_at-filter>
            <input
              v-model="columnSearchKeys.mobile_last_used_at.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #kiosk_pin="{ item }">
            <td class="mw-auto lh-normal text-center">
              {{
                item.kiosk_users && item.kiosk_users.kpasscode === "Y"
                  ? "Y"
                  : ""
              }}
            </td>
          </template>
        </DataTable>

        <Pagination
          v-if="
            pagination &&
            pagination.pages &&
            pagination.pages > 1 &&
            !isLazyLoadingMode
          "
          class="cs-pagination mt-4"
          :dots="false"
          :double-arrows="true"
          :active-page="pagination.activePage"
          :pages="pagination.pages"
          size="lg"
          align="center"
          @update:active-page="setActivePage"
        >
          <template #last-button>
            <i class="flaticon-right-arrow-1" />
          </template>
          <template #first-button>
            <i class="flaticon-left-arrow-2" />
          </template>
          <template #previous-button>
            <i class="fi flaticon-left-arrow-1" />
          </template>
          <template #next-button>
            <i class="fi flaticon-chevron" />
          </template>
        </Pagination>
      </CCol>
    </CRow>
  </CContainer>
</template>

<script>
import Loader from "@/components/Loader"
import { mapGetters } from "vuex"
import moment from "moment-timezone"
import PerPageSelector from "../PerPageSelector.vue"
import HelpCenterButton from "@/components/HelpCenterButton"

export default {
  name: "UsersDataTable",
  components: {
    Loader,
    PerPageSelector,
    HelpCenterButton
  },
  emits: ["setUserEditable"],
  data() {
    return {
      isActiveSyncBtn: null,
      isLazyLoadingMode: false,
      isLoading: false,
      isLoadingBtn: false,
      syncButtonText: "Sync Users",
      bulkArchiveSuccessMessage:
        "Your request to bulk archive has begun. If the action can be executed users will move from the Main Users page to the Archived User page. This will take some time to complete.",
      reqResponse: {
        type: "",
        message: "",
        errors: {}
      },
      formData: {
        loginType: [
          {
            value: 1,
            label: "InHouse SSO"
          },
          {
            value: 10,
            label: "Google"
          },
          {
            value: 20,
            label: "Microsoft"
          },
          {
            value: 101,
            label: "ClassLink"
          },
          {
            value: 102,
            label: "Clever"
          },
          {
            value: 103,
            label: "GG4L"
          }
        ]
      },
      selectedUsers: [],
      pagination: {
        activePage: 1,
        total: 0,
        pages: 0,
        per_page: { label: "25", value: 25 }
      },
      tableTabs: [
        {
          label: "Users",
          value: "users"
        }
      ],
      activeTableTab: {
        value: "users"
      },
      filterParams: {
        sort_query: "",
        searchQuery: "",
        student_sis_id: false
      },
      dataTableFields: [
        { key: "select", label: "", filter: false },
        { key: "new", label: "New", filter: false },
        { key: "action", label: "Action", filter: false },
        {
          key: "student_sis_id",
          filter: false,
          sorter: false,
          label: "Student number",
          _style: "min-width:150px"
        },
        { key: "first_name", _style: "min-width:120px", label: "First Name" },
        { key: "last_name", _style: "min-width:120px", label: "Last Name" },
        { key: "email", _style: "min-width:100px", label: "Email" },
        { key: "role_id", label: "Role" },
        { key: "grade_year", _style: "min-width:150px", label: "Grad Year" },
        // {key: 'auth_type', _style:'min-width:150px', label: 'Login type'},

        {
          key: "web_last_login_at",
          label: "Web Last Login",
          _style: "min-width:180px"
        },
        {
          key: "mobile_last_login_at",
          label: "Mobile Last Login",
          _style: "min-width:180px"
        },
        {
          key: "mobile_last_used_at",
          label: "Mobile Last Used",
          _style: "min-width:180px"
        },

        { key: "status", label: "Status", filter: false },
        {
          key: "kiosk_pin",
          label: "Kiosk PC",
          _style: "min-width:100px",
          filter: false
        }
      ],
      columnSearchKeys: {
        globalQuery: "",
        first_name: { value: "", column: ["first_name"], sortBy: "first_name" },
        last_name: { value: "", column: ["last_name"], sortBy: "last_name" },
        email: { value: "", column: ["email"], sortBy: "email" },
        role_id: {
          value: "",
          column: ["role_user.name"],
          sortBy: "role_user.name"
        },
        grade_year: {
          value: "",
          column: ["role_user.pivot.grade_year"],
          sortBy: "role_user.pivot.grade_year.raw"
        },
        auth_type: {
          value: "",
          column: ["auth_type_name"],
          sortBy: "auth_type"
        },
        web_last_login_at: {
          value: "",
          column: ["web_last_login_at"],
          sortBy: "web_last_login_at"
        },
        mobile_last_login_at: {
          value: "",
          column: ["mobile_last_login_at"],
          sortBy: "mobile_last_login_at"
        },
        mobile_last_used_at: {
          value: "",
          column: ["mobile_last_used_at"],
          sortBy: "mobile_last_used_at"
        }
      },
      allAuthTypes: [
        {
          id: 0,
          icon: "user",
          title: "Manual"
        },
        {
          id: 10,
          icon: "google",
          title: "Google",
          html: '<span class="path1"></span><span class="path2"></span><span class="path3"></span><span class="path4"></span></span>'
        },
        {
          id: 20,
          icon: "microsoft",
          title: "Microsoft",
          html: '<span class="path1"></span><span class="path2"></span><span class="path3"></span><span class="path4"></span><span class="path5"></span><span class="path6"></span><span class="path7"></span>'
        },
        {
          id: 101,
          icon: "sso",
          title: "ClassLink"
        },
        {
          id: 102,
          icon: "clever",
          title: "Clever"
        },
        {
          id: 103,
          icon: "gg4l",
          title: "GG4L"
        }
      ],
      activeAvatar: null,
      showArchivedUsersModal: null,
      areAllEntriesChecked: false
    }
  },
  computed: {
    ...mapGetters({
      users: "users/users",
      authProviders: "schools/activeSchoolAuthProviders",
      user: "authentication/user",
      synchronization: "users/synchronization",
      userRole: "authentication/getUserRole",
      activeSchool: "schools/activeSchool"
    }),
    isSuperAdmin() {
      return this.user.role.name == "admin" && this.user.auth_type == 1001
    },
    isSyncButtonDisabled() {
      return (
        this.synchronization &&
        this.synchronization.status &&
        this.synchronization.status !== "DONE"
      )
    },
    isActiveSyncVisible() {
      return (
        this.isSuperAdmin &&
        !this.isSyncButtonDisabled &&
        this.synchronization &&
        this.synchronization.status
      )
    },
    authTypes() {
      return this.authProviders
        ? this.allAuthTypes.filter((el) => this.authProviders.includes(el.id))
        : null
    },
    activeDataFields() {
      return this.dataTableFields.filter((field) =>
        !this.filterParams.student_sis_id
          ? field.key !== "student_sis_id"
          : field
      )
    },
    provideUsersByApiOnly() {
      return (
        this.activeSchool && this.activeSchool.provide_users_by_api_only == 1
      )
    }
  },
  mounted() {
    this.initLazyLoading()
    this.handleUsers()
    if (this.isSuperAdmin) {
      this.getSynchronization()
    }
  },
  methods: {
    syncUsers(id) {
      this.isActiveSyncBtn = false
      this.syncButtonText = "Processing"
      this.isLoadingBtn = true
      this.$store
        .dispatch("users/synchronization", id)
        .then(() => {
          this.getSynchronization()
          this.isLoadingBtn = true
        })
        .catch((err) => {
          const response = err.response.data
          this.setErrorResponse(response.message, response.errors)
        })
    },
    getSynchronization() {
      this.$store.dispatch("users/getSynchronization").then(() => {
        if (
          this.synchronization.status !== "DONE" &&
          this.synchronization.status !== "FAILED"
        ) {
          setTimeout(() => {
            this.getSynchronization()
          }, 30000)
        } else {
          this.isLoadingBtn = false
          this.isActiveSyncBtn = true
          this.syncButtonText = "Sync Users"
        }
      })
    },
    initLazyLoading() {
      const body = document.getElementsByClassName("main-scroll-container")[0]
      if (body) {
        body.onscroll = () => {
          if (
            this.isLazyLoadingMode &&
            this.users &&
            this.users.length &&
            this.pagination.total > this.users.length
          ) {
            //todo check this
            //this magic number +1 is added for the lazy loading in small resolution. Perhaps some container is overflowing
            if (body.offsetHeight + body.scrollTop + 1 >= body.scrollHeight) {
              this.pagination.activePage = this.pagination.activePage + 1
              this.handleUsers(this.pagination.activePage, true)
            }
          }
        }
      }
    },
    handleUsers(page, isLazyLoadingMode, noLoader, isExport) {
      this.isLoading = !noLoader
      this.pagination.activePage = page ? page : 1
      const params = {
        page: this.pagination.activePage,
        per_page: this.pagination.per_page.value,
        archived: false
      }
      if (this.filterParams.sort_query) {
        params.sort = this.filterParams.sort_query
      }
      if (this.filterParams.searchQuery) {
        params.search_query = this.filterParams.searchQuery
      }
      if (isExport) {
        this.exportUsers(params)
      } else {
        this.getUsers(params, isLazyLoadingMode)
      }
    },
    getUsers(params, isLazyLoadingMode) {
      this.$store
        .dispatch("users/getUsers", params)
        .then((response) => {
          const data = response.data
          if (isLazyLoadingMode) {
            this.$store.commit("users/PUSH_USERS", data.data)
          } else {
            this.$store.commit("users/SET_USERS", data.data)
          }
          const meta = data.meta
          if (meta) {
            this.pagination.total = meta.total
            this.pagination.from = meta.from
            this.pagination.to = meta.to
            this.pagination.pages = Math.ceil(meta.total / meta.per_page)
            this.pagination.activePage = Number(this.pagination.activePage)
            this.pagination = JSON.parse(JSON.stringify(this.pagination))
          }
          this.isLoading = false
        })
        .catch(() => {
          this.isLoading = false
        })
    },
    selectUsers(e) {
      if (e.target.checked) {
        this.selectedUsers = []
        this.users.forEach((user) => {
          this.selectedUsers.push(user.id)
        })
      } else {
        this.selectedUsers = []
      }
    },
    inviteBulk() {
      this.$store
        .dispatch("users/inviteBulk", { users: this.selectedUsers })
        .then(() => {
          this.handleUsers()
          this.selectedUsers = []
        })
    },
    archive() {
      if (Object.keys(this.selectedUsers).length == 1) {
        this.$store
          .dispatch("users/archiveSingle", this.selectedUsers[0])
          .then(() => {
            setTimeout(() => {
              this.handleUsers()
              this.setSuccessResponse("Successfully archived!")
              this.isLoading = false
              this.showArchivedUsersModal = true
            }, 500)
            this.selectUsers = []
          })
          .catch((err) => {
            const response = err.response.data
            this.setErrorResponse(response.message, response.errors)
            this.isLoading = false
            this.showArchivedUsersModal = true
          })
      } else {
        this.$store
          .dispatch("users/archiveBulk", { users: this.selectedUsers.join() })
          .then(() => {
            setTimeout(() => {
              this.handleUsers()
            }, 500)
            this.selectUsers = []
            this.setSuccessResponse(this.bulkArchiveSuccessMessage)
            this.isLoading = false
            this.showArchivedUsersModal = true
          })
          .catch((err) => {
            const response = err.response.data
            this.setErrorResponse(response.message, response.errors)
            this.isLoading = false
            this.showArchivedUsersModal = true
          })
      }
    },
    exportUsers(params) {
      this.isLoading = true
      params.per_page = [25, 50, 100].includes(this.pagination.per_page.value)
        ? this.pagination.per_page.value
        : "all"
      this.$store
        .dispatch("users/exportUsers", {
          ...params,
          is_substitute: false,
          is_archived: false,
          all_entries: this.areAllEntriesChecked,
          student_sis_id: true
        })
        .then((response) => {
          if (response.data) {
            this.$download.CSVExport(response.data, "users")
          }
          this.isLoading = false
        })
        .catch(() => {
          this.isLoading = false
        })
    },

    editUser(user) {
      this.$emit("setUserEditable")
      this.$store.commit("users/SET_EDITABLE_USER", user)
      const topPosition = document.getElementById("user-manage-form-container")
        .offsetParent.offsetTop
      this.$helpers.scrollToTop(topPosition ? topPosition - 100 : 0)
    },
    recreateUser(user) {
      this.$store
        .dispatch("users/recreateUser", user)
        .then(() => {
          this.resetForm()
        })
        .catch((err) => {
          this.isLoading = false
          const response = err.response.data
          this.setErrorResponse(response.message, response.errors)
        })
    },
    sortByColumn(column) {
      const columnName = this.columnSearchKeys[column.column]
        ? this.columnSearchKeys[column.column].sortBy
        : column.column
      this.filterParams.sort_query = `${columnName}:${
        column.asc ? "asc" : "desc"
      }`
      if (column.column == "role_id" || column.column == "grade_year") {
        this.filterParams.searchQuery = this.filterParams.searchQuery.includes(
          "role_user.pivot.school_id"
        )
          ? this.filterParams.searchQuery
          : this.filterParams.searchQuery
          ? this.filterParams.searchQuery +
            `,role_user.pivot.school_id:${this.user.school_id}`
          : `role_user.pivot.school_id:${this.user.school_id}`
      }
      this.handleUsers()
    },
    isCreatedToday(date) {
      return moment(date).isSame(moment(), "d")
    },
    getAuthType(id) {
      switch (id) {
        case 1:
          return "Manual"
        case 10:
          return "Google"
        case 20:
          return "Microsoft"
        case 101:
          return "ClassLink"
        case 102:
          return "Clever"
        case 103:
          return "GG4L"
        default:
          return ""
      }
    },
    searchByQuery(value, type, minLetters) {
      clearInterval(this.searchTimeOut)
      this.searchTimeOut = setTimeout(() => {
        this.handleQuerySearch(value, type, minLetters)
      }, 500)
    },
    handleQuerySearch(value, type, minLetters) {
      const query = value.toString()
      const minLen = minLetters ? parseInt(minLetters) : 0
      if (
        query.replace(/\s/g, "").length > minLen ||
        query.replace(/\s/g, "").length === 0
      ) {
        this.filterParams.searchQuery = ""
        if (type && type === "column") {
          this.columnSearchKeys.globalQuery = ""
          for (const key in this.columnSearchKeys) {
            if (Object.hasOwnProperty.call(this.columnSearchKeys, key)) {
              const columnData = this.columnSearchKeys[key]
              if (columnData.value) {
                columnData.column.forEach((col) => {
                  if (
                    col == "role_user.name" ||
                    col == "role_user.pivot.grade_year"
                  ) {
                    this.filterParams.searchQuery =
                      this.filterParams.searchQuery +
                      `role_user.pivot.school_id:${this.user.school_id}, `
                  }
                  this.filterParams.searchQuery =
                    this.filterParams.searchQuery +
                    `${col}:"${columnData.value}", `
                })
              }
            }
          }
          this.filterParams.searchQuery = this.filterParams.searchQuery.slice(
            0,
            -2
          )
        } else {
          this.resetSearchQuery()
          this.columnSearchKeys.globalQuery = query
          this.filterParams.searchQuery = query ? '"' + query + '"' : query
        }
        this.handleUsers(null, false)
      }
    },
    resetSearchQuery() {
      this.filterParams.searchQuery = ""
      this.columnSearchKeys = {
        globalQuery: "",
        first_name: { value: "", column: ["first_name"], sortBy: "first_name" },
        last_name: { value: "", column: ["last_name"], sortBy: "last_name" },
        email: { value: "", column: ["email"], sortBy: "email" },
        role_id: {
          value: "",
          column: ["role_user.name"],
          sortBy: "role_user.name"
        },
        grade_year: {
          value: "",
          column: ["role_user.pivot.grade_year"],
          sortBy: "role_user.pivot.grade_year.raw"
        },
        auth_type: {
          value: "",
          column: ["auth_type_name"],
          sortBy: "auth_type"
        },
        web_last_login_at: {
          value: "",
          column: ["web_last_login_at"],
          sortBy: "web_last_login_at"
        },
        mobile_last_login_at: {
          value: "",
          column: ["mobile_last_login_at"],
          sortBy: "mobile_last_login_at"
        },
        mobile_last_used_at: {
          value: "",
          column: ["mobile_last_used_at"],
          sortBy: "mobile_last_used_at"
        }
      }
    },
    setPerPage(option) {
      if (option) {
        this.isLazyLoadingMode = option.label === "All entries"
        this.areAllEntriesChecked = option.label === "All entries"
        this.pagination.per_page = option
        this.handleUsers()
      }
    },
    matchAuthType(value) {
      if (!value.length) {
        this.columnSearchKeys.auth_type.value = ""
        this.searchByQuery("", "column")
        return
      }
      const authType = this.formData.loginType.filter((loginType) =>
        loginType.label.includes(value)
      )
      if (authType.length) {
        this.columnSearchKeys.auth_type.value = authType[0].value
        this.searchByQuery(authType[0].value, "column")
      } else {
        this.columnSearchKeys.auth_type.value = ""
        this.searchByQuery("", "column")
      }
    },
    refreshDataTable() {
      this.resetSearchQuery()
      this.handleUsers(1, false, true)
    },
    setActivePage(page) {
      this.pagination.activePage = page
      this.handleUsers(this.pagination.activePage)
      this.$helpers.scrollToTop()
    },
    getUserStatus(user) {
      if (!user.is_synced) {
        return false
      } else if (user.invited_at == null && user.send_invite == 1) {
        return "IN PROGRESS"
      } else if (user.invited_at != null && user.send_invite == 0) {
        return "INVITED"
      } else if (this.isCreatedToday(user.created_at) && user.is_synced) {
        return "ADDED"
      }
      return null
    },
    showAvatar(avatar) {
      this.activeAvatar = avatar
    },
    resetResponseMessages() {
      this.reqResponse = {
        type: "",
        message: "",
        errors: {}
      }
    },
    setSuccessResponse(message) {
      this.reqResponse = {
        type: "success",
        message: message ? message : "Success!",
        errors: {}
      }
    },
    setErrorResponse(message, errors) {
      this.reqResponse = {
        type: "danger",
        message: message ? message : "Something went wrong!",
        errors: errors ? errors : {}
      }
    }
  }
}
</script>
